import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/Loading';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { FadeIn } from '@/components/ui/AnimatedCard';
import { Modal } from '@/components/ui/Modal';
import { TemplateForm } from '@/components/lists/TemplateForm';
import { TemplatesList } from '@/components/lists/TemplatesList';
import { TemplateItemManager } from '@/components/lists/TemplateItemManager';
import { TemplateQuickItemEntry } from '@/components/lists/TemplateQuickItemEntry';
import { UseTemplateModal } from '@/components/lists/UseTemplateModal';
import { TemplateFiltersComponent, TemplateFilters, TemplateSort } from '@/components/lists/TemplateFilters';
import { DeleteConfirmModal } from '@/components/shared/DeleteConfirmModal';
import { useTemplatesStore } from '@/store/templatesStore';
import { useNavigationStore } from '@/store/navigationStore';
import { ListTemplate, TemplateCategory } from '@/types/lists';
import { Plus, FileStack } from 'lucide-react';

// Default filter and sort configurations
const defaultFilters: TemplateFilters = {};
const defaultSort: TemplateSort = { field: 'title', direction: 'asc' };

// Filter function for templates
function filterTemplates(templates: ListTemplate[], filters: TemplateFilters): ListTemplate[] {
  let filtered = [...templates];

  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(template =>
      template.title.toLowerCase().includes(searchLower) ||
      template.description.toLowerCase().includes(searchLower)
    );
  }

  // Apply category filter
  if (filters.category?.length) {
    filtered = filtered.filter(template =>
      filters.category!.includes(template.category as TemplateCategory)
    );
  }

  return filtered;
}

// Sort function for templates
function sortTemplates(templates: ListTemplate[], sort: TemplateSort): ListTemplate[] {
  return [...templates].sort((a, b) => {
    let aValue: unknown;
    let bValue: unknown;

    switch (sort.field) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      case 'usageCount':
        aValue = a.usageCount;
        bValue = b.usageCount;
        break;
      case 'updatedAt':
        aValue = new Date(a.updatedAt).getTime();
        bValue = new Date(b.updatedAt).getTime();
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      default:
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sort.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });
}

export function ListTemplates() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const {
    templates,
    isLoading,
    error,
    initializeTemplates,
    deleteTemplate
  } = useTemplatesStore();

  const { setPageInfo } = useNavigationStore();

  // Local state for filters, sort, and form management
  const [filters, setFilters] = useState<TemplateFilters>(defaultFilters);
  const [sort, setSort] = useState<TemplateSort>(defaultSort);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ListTemplate | null>(null);
  const [useTemplateTarget, setUseTemplateTarget] = useState<ListTemplate | null>(null);
  const [showQuickItemEntry, setShowQuickItemEntry] = useState(false);

  // Find selected template from URL params (consistent with Lists page)
  const selectedTemplate = templateId
    ? templates.find(t => t.id === templateId)
    : null;

  // Debug logging for template not found
  useEffect(() => {
    if (templateId) {
      if (!selectedTemplate && templates.length > 0) {
        console.error('❌ Template not found!', {
          searchingFor: templateId,
          availableIds: templates.map(t => t.id),
        });
      }
    }
  }, [templateId, templates, selectedTemplate]);

  // Open create form
  const openCreateForm = useCallback(() => {
    setEditingTemplateId(null);
    setIsFormOpen(true);
  }, []);

  // Set page info based on view (matches Lists page pattern)
  useEffect(() => {
    if (selectedTemplate) {
      setPageInfo(selectedTemplate.title, {
        label: 'New Item',
        icon: Plus,
        onClick: () => setShowQuickItemEntry(true),
      });
    } else {
      setPageInfo('List Templates', {
        label: 'New Template',
        icon: Plus,
        onClick: openCreateForm,
      });
    }
  }, [setPageInfo, selectedTemplate, openCreateForm]);

  // Initialize data on mount if empty
  useEffect(() => {
    if (templates.length === 0) {
      initializeTemplates();
    }
  }, [templates.length, initializeTemplates]);

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    const filtered = filterTemplates(templates, filters);
    return sortTemplates(filtered, sort);
  }, [templates, filters, sort]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  // Close form
  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingTemplateId(null);
  }, []);

  // Clone handler - opens modal for goal selection
  const handleTemplateClone = (template: ListTemplate) => {
    setUseTemplateTarget(template);
  };

  const handleCloseUseTemplateModal = () => {
    setUseTemplateTarget(null);
  };

  // Edit handler
  const openEditForm = (template: ListTemplate) => {
    setEditingTemplateId(template.id);
    setIsFormOpen(true);
  };

  // Delete handler
  const handleDeleteTemplate = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteTemplate(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  // Filter change handler
  const handleFiltersChange = (newFilters: TemplateFilters) => {
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <Container size="full" className="py-6">
        <LoadingState title="Loading templates..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="full" className="py-6">
        <ErrorState
          title="Failed to load templates"
          description={error}
          action={{
            label: "Try Again",
            onClick: initializeTemplates
          }}
        />
      </Container>
    );
  }

  const hasTemplates = templates.length > 0;

  // Handle template click to navigate (consistent with Lists page)
  const handleTemplateClick = (template: ListTemplate) => {
    navigate(`/list-templates/${template.id}`);
  };

  return (
    <Container size="full" className="py-6">
      <Stack spacing="lg">
        {/* Template Detail View */}
        {selectedTemplate ? (
          <FadeIn>
            <TemplateItemManager template={selectedTemplate} />
          </FadeIn>
        ) : (
          /* Template List View */
          hasTemplates ? (
            <Stack spacing="lg">
              <TemplateFiltersComponent
                filters={filters}
                sort={sort}
                onFiltersChange={handleFiltersChange}
                onSortChange={setSort}
                onClearFilters={clearFilters}
                totalCount={templates.length}
                filteredCount={filteredTemplates.length}
              />

              {filteredTemplates.length > 0 ? (
                <TemplatesList
                  templates={filteredTemplates}
                  onClone={handleTemplateClone}
                  onEdit={openEditForm}
                  onDelete={setDeleteConfirm}
                  onClick={handleTemplateClick}
                />
              ) : (
                <Card>
                  <CardContent className="py-12">
                    <EmptyState
                      title="No templates match your filters"
                      description="Try adjusting your search or filter criteria to see more results."
                      action={{
                        label: "Clear Filters",
                        onClick: clearFilters
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </Stack>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={<FileStack className="h-12 w-12 text-muted-foreground" />}
                  title="No templates yet"
                  description="Create reusable templates to quickly generate new lists with predefined items and structure."
                  action={{
                    label: "Create Your First Template",
                    onClick: openCreateForm
                  }}
                />
              </CardContent>
            </Card>
          )
        )}
      </Stack>

      {/* Create/Edit Form Modal */}
      <TemplateForm
        isOpen={isFormOpen}
        onClose={closeForm}
        templateId={editingTemplateId || undefined}
        isEditing={!!editingTemplateId}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteTemplate}
        title="Delete Template"
        itemName={deleteConfirm?.title || ''}
        itemType="template"
        description="This template will be permanently deleted. Lists created from this template will not be affected."
      />

      {/* Use Template Modal */}
      <UseTemplateModal
        isOpen={useTemplateTarget !== null}
        onClose={handleCloseUseTemplateModal}
        template={useTemplateTarget}
      />

      {/* Quick Item Entry Modal (matches Lists page pattern) */}
      {selectedTemplate && (
        <Modal
          isOpen={showQuickItemEntry}
          onClose={() => setShowQuickItemEntry(false)}
          title={`Add Item to ${selectedTemplate.title}`}
        >
          <TemplateQuickItemEntry
            templateId={selectedTemplate.id}
            placeholder="Enter item content..."
            onItemAdded={() => setShowQuickItemEntry(false)}
            autoFocus={true}
          />
        </Modal>
      )}
    </Container>
  );
}
